import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Pencil, LayoutGrid, Diamond, MoveUpRight, BookOpen } from "lucide-react";
import BrandMark from "../components/BrandMark";
import FaqSection, { faqJsonLd } from "../components/FaqSection";
import { api } from "../lib/api";

const CATEGORY_LABEL = {
  "findings":     "Finding",
  "teardowns":    "Teardown",
  "reports":      "Report",
  "case-studies": "Case Study",
  "methodology":  "Methodology",
  "resources":    "Resource",
};

const dimensions = [
  { n: "01", title: "Entity Recognition",      body: "Can AI accurately identify who the company is? How confidently and correctly does it describe your business, what you do, and who you serve across all four platforms." },
  { n: "02", title: "AI Discovery Presence",   body: "Can AI discover and recommend the company? When AI is asked to name the leading firms in your category and region, do you appear at all — and at what position? Position 1 means category authority. Missing entirely means invisible. Measured across ChatGPT, Gemini, Claude, and Perplexity." },
  { n: "03", title: "Competitive Positioning", body: "Can AI compare the company against competitors? Is your business grouped with the right peer set, and does AI explain how you differ? Clustering determines your perceived tier." },
  { n: "04", title: "Accuracy & Consistency",  body: "Are descriptions correct and consistent? Do all four AI platforms describe you the same way? Contradictions and gaps signal weak entity scaffolding — a core visibility problem." },
  { n: "05", title: "Authority & Confidence",  body: "Does AI have enough evidence to speak confidently? Citations, case studies, awards, certifications, and leadership presence all feed AI's confidence in your entity." },
];

const journey = [
  { glyph: "◌", label: "Invisible" },
  { glyph: "◎", label: "Described" },
  { glyph: "◉", label: "Recognized" },
  { glyph: "⊕", label: "Recommended" },
  { glyph: "★", label: "Dominant" },
];

const tiers = [
  { range: "0–30",  label: "Invisible",         body: "AI cannot confidently describe or recommend you",       dot: "#c46a5a" },
  { range: "31–50", label: "Underrepresented", body: "Basic description only — not included in recommendations", dot: "#d4a04a" },
  { range: "51–70", label: "Emerging",          body: "Occasionally included — inconsistent across platforms",  dot: "#b8963e" },
  { range: "71+",   label: "Recommended",       body: "Consistently recognized and actively recommended by AI", dot: "#6db66d" },
];

const gaps = [
  { title: "Weak Category Anchoring",   body: "AI doesn't clearly associate you with a service category — so you're excluded from category-level recommendations entirely." },
  { title: "Missing Entity Scaffolding", body: "No structured data, no canonical brand description, inconsistent presence across the web. AI can't form a confident model of who you are." },
  { title: "Thin Authority Signals",     body: "AI describes you with hedging language because it finds no citations, case studies, awards, or recognized expertise to anchor confidence." },
  { title: "Weak Competitive Clustering", body: "You're not grouped with your actual competitors in AI outputs — which signals low market standing to the model." },
];

const wins = [
  { title: "Add Organization + Service Schema",       body: "Structured data gives AI a direct, machine-readable signal about who you are, what you do, and where you operate." },
  { title: "Create an AI-Readable Entity Block",      body: "A canonical paragraph on your site that defines your company in the exact language AI systems use to build entity models." },
  { title: "Publish AI-Optimized Service Summaries", body: "Clear, structured service descriptions that anchor your category inclusion and give AI accurate content to draw from." },
  { title: "Add Expertise & Authority Signals",       body: "Case studies, certifications, awards, and leadership bios give AI the proof points it needs to recommend you with confidence." },
  { title: "Standardize Your Brand Positioning",      body: "Consistent descriptions across your website, directories, press mentions, and partner pages build a coherent entity signal." },
];

const steps = [
  { n: "01", icon: Pencil,      title: "You Submit",          body: "Company name, website, industry. 30 seconds. That's all we need to start." },
  { n: "02", icon: LayoutGrid,  title: "We Run the Machine",  body: "We run standardized prompts across all four AI platforms — entity, category, and competitive queries — and record every output." },
  { n: "03", icon: Diamond,     title: "We Score & Analyze",  body: "Each output is scored across the five dimensions of the AI Visibility Score™. Gaps are identified. Quick wins are ranked by impact." },
  { n: "04", icon: MoveUpRight, title: "You Get the Snapshot", body: "A one-page PDF with your score, tier, visibility gaps, consensus summary, and five customized quick wins. Delivered instantly." },
];

const platforms = [
  { name: "ChatGPT",    by: "OpenAI" },
  { name: "Gemini",     by: "Google" },
  { name: "Claude",     by: "Anthropic" },
  { name: "Perplexity", by: "AI Search" },
];

const comparison = [
  ["Optimizes for search rankings",       "Optimizes for AI comprehension"],
  ["Keyword-based signals",                "Entity & authority signals"],
  ["Page-level optimization",              "Company-level recognition"],
  ["Ranks in blue links",                  "Recommended in AI responses"],
  ["Traffic metrics",                       "AI Visibility Score™"],
];

const HOMEPAGE_FAQS = [
  { q: "What is AI Visibility?",                                a: "AI Visibility is the measurable degree to which AI systems like ChatGPT, Claude, Gemini, and Perplexity recognize, categorize, and recommend your business. It is the new front door of buyer discovery." },
  { q: "What is an AI Visibility Audit?",                        a: "An AI Visibility Audit is a structured, multi-platform measurement of how the four major AI assistants describe and recommend your business. The standard audit runs 20 prompts; the full audit runs 48 prompts across ChatGPT, Claude, Gemini, and Perplexity." },
  { q: "How do companies appear in ChatGPT?",                    a: "ChatGPT surfaces companies that meet three criteria: clean entity scaffolding in its training corpus, structured data the model can ingest deterministically, and trusted third-party citations that confirm category and specialty." },
  { q: "Why does AI recommend competitors?",                     a: "AI defaults to the firms with the strongest entity scaffolding — clear canonical descriptions, Schema.org markup, and trade-press citations. AI does not weight revenue or company size; it weights entity clarity. Competitors with thinner records can still outscore you on AI Visibility." },
  { q: "What is Entity SEO?",                                    a: "Entity SEO is the practice of becoming a recognizable, unambiguous entity in the knowledge graphs that Google and AI systems share. The three pillars are canonical description, Schema.org markup, and trusted third-party citations." },
  { q: "How is AI Visibility different from SEO?",               a: "SEO optimizes for a ranking position on a search engine results page. AI Visibility optimizes for inclusion in the AI's single generated answer. The scorecards are different and the tactics that move one do not always move the other — though Schema.org markup and canonical descriptions help both." },
  { q: "How long does an AI Visibility Audit take?",             a: "The free AI Visibility Snapshot™ is delivered instantly. The Full AI Visibility Audit™ ($300, 48 data points, 10-page deliverable) is delivered in 48 hours." },
  { q: "What platforms are evaluated?",                          a: "All four major AI assistants: ChatGPT (OpenAI), Claude (Anthropic), Gemini (Google), and Perplexity. 5 prompts per platform for the Snapshot; 12 prompts per platform for the Full Audit." },
  { q: "What is an AI Visibility Score™?",                       a: "The AI Visibility Score™ is a 0-100 composite of five dimensions (Entity Recognition, AI Discovery Presence, Competitive Positioning, Accuracy & Consistency, Authority & Confidence) scored across all four LLM platforms." },
  { q: "Can AI visibility be improved?",                          a: "Yes — typically by +10 to +18 points within 60 days. The fastest lift comes from Schema.org markup, a canonical brand description used verbatim across owned media, and a deliberate trade-press citation pipeline. Most clients move at least one tier within 90 days of executing the audit roadmap." },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/research/featured")
      .then(({ data }) => { if (alive) setFeatured((data?.featured || []).slice(0, 3)); })
      .catch(() => { /* non-critical — hide the section if it fails */ });
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="landing-page" className="relative">
      <Helmet>
        <title>The Entity Agency — AI Visibility Intelligence for Businesses</title>
        <meta name="description" content="Understand how AI systems describe, categorize, and recommend your business. The Entity Agency audits ChatGPT, Claude, Gemini, and Perplexity — then scores it on a 0-100 framework. Snapshot delivered instantly." />
        <link rel="canonical" href="https://theentityagency.com/" />
        <meta property="og:title" content="The Entity Agency — AI Visibility Intelligence for Businesses" />
        <meta property="og:description" content="AI Visibility audits and scoring for businesses that want to be recommended by ChatGPT, Claude, Gemini, and Perplexity." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://theentityagency.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(HOMEPAGE_FAQS))}</script>
      </Helmet>
      {/* ════════ HERO ════════ */}
      <section className="relative overflow-hidden border-b border-[#2a2620]">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-[0.08] pointer-events-none"
          style={{ background: "radial-gradient(circle at center, #b8963e 0%, transparent 60%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-20 sm:pt-28 pb-24 sm:pb-32 relative">
          <div className="animate-fade-up">
            <BrandMark size={64} data-testid="hero-brand-mark" />
          </div>
          <div className="mt-8 overline animate-fade-up">
            The Entity Agency · AI Visibility Intelligence
          </div>
          <h1
            data-testid="hero-title"
            className="mt-6 font-serif-display tracking-tight text-[40px] sm:text-[60px] lg:text-[80px] leading-[0.98] text-[#f4efe6] max-w-5xl animate-fade-up-100"
            style={{ fontWeight: 600 }}
          >
            Become more visible to AI. <br className="hidden sm:block" />
            <em className="text-[#b8963e] italic font-normal">Operate better with AI.</em>
          </h1>
          <p
            data-testid="hero-sub"
            className="mt-8 text-lg text-[#c9bca4] max-w-3xl leading-relaxed animate-fade-up-200"
          >
            The Entity Agency helps businesses understand how AI platforms represent their company and install practical AI-supported workflows that save time, improve follow-up, and strengthen operations.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3 animate-fade-up-300">
            <Link to="/signup" data-testid="hero-cta-primary" className="btn-gold">
              Run Your Free AI Visibility Snapshot <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/book-efficiency-review" data-testid="hero-cta-secondary" className="btn-ghost">
              Book a Free AI Efficiency Review →
            </Link>
          </div>
          <p className="mt-6 font-mono-eav text-[11px] tracking-[0.18em] uppercase text-[#8a7e6d] max-w-xl">
            Built for small businesses, local companies, nonprofits, and growing teams without internal AI departments.
          </p>

          {/* Stat row */}
          <div className="mt-16 grid grid-cols-3 gap-4 max-w-3xl animate-fade-up-400">
            {[
              ["4",   "AI Platforms Audited"],
              ["5",   "Scoring Dimensions"],
              ["48h", "Snapshot Delivery"],
            ].map(([n, l]) => (
              <div key={l}>
                <p className="font-serif-display text-5xl sm:text-6xl text-[#f4efe6]" style={{fontWeight: 700}}>
                  {n}
                </p>
                <p className="mt-2 font-mono-eav text-[10px] tracking-[0.18em] uppercase text-[#8a7e6d]">
                  {l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ TWO DIVISIONS ════════ */}
      <section id="divisions" className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-28">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
            {/* Card 1 — AI Visibility Intelligence */}
            <div
              data-testid="division-card-visibility"
              className="group border border-[#2a2620] hover:border-[#b8963e] transition-colors bg-[#0e0c0a] p-8 sm:p-10 flex flex-col"
            >
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">
                Division 01 · AI Visibility Intelligence
              </p>
              <h3 className="mt-5 font-serif-display text-[28px] sm:text-[34px] leading-[1.1] text-[#f4efe6]"
                  style={{ fontWeight: 600 }}>
                Understand how AI sees your business.
              </h3>
              <p className="mt-4 text-[#c9bca4] leading-relaxed">
                See how ChatGPT, Claude, Gemini, Perplexity, and other AI systems recognize, describe, compare, and recommend your company.
              </p>
              <ul className="mt-6 space-y-2 text-[15px] text-[#c9bca4]">
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Identify inaccurate business descriptions</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Find recommendation and category gaps</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Compare visibility against competitors</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Improve AI discovery readiness</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Monitor changes over time</li>
              </ul>
              <div className="mt-auto pt-8">
                <Link to="/signup" data-testid="division-cta-visibility" className="btn-gold">
                  Run Your Free Snapshot <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Card 2 — AI Operations Integration */}
            <div
              data-testid="division-card-operations"
              className="group border border-[#2a2620] hover:border-[#b8963e] transition-colors bg-[#0e0c0a] p-8 sm:p-10 flex flex-col"
            >
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">
                Division 02 · AI Operations Integration
              </p>
              <h3 className="mt-5 font-serif-display text-[28px] sm:text-[34px] leading-[1.1] text-[#f4efe6]"
                  style={{ fontWeight: 600 }}>
                Install practical AI systems in your business.
              </h3>
              <p className="mt-4 text-[#c9bca4] leading-relaxed">
                Reduce repetitive work, respond to customers faster, improve follow-up, and give employees better tools without replacing the human relationships that make your business valuable.
              </p>
              <ul className="mt-6 space-y-2 text-[15px] text-[#c9bca4]">
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Respond to leads faster</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Reduce administrative work</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Create proposals and communications faster</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Organize business information</li>
                <li className="flex gap-3"><span className="text-[#b8963e]">›</span> Improve employee consistency</li>
              </ul>
              <div className="mt-auto pt-8 flex flex-wrap gap-3">
                <Link to="/book-efficiency-review" data-testid="division-cta-operations" className="btn-gold">
                  Book a Free AI Efficiency Review <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/ai-operations" className="btn-ghost">
                  Learn more →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ AI VISIBILITY JOURNEY ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-28">
          <p className="overline">The AI Visibility Journey™</p>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-y-8 gap-x-3">
            {journey.map((stop, i) => (
              <React.Fragment key={stop.label}>
                <div className="flex flex-col items-center">
                  <span className="font-serif-display text-5xl text-[#b8963e] leading-none">{stop.glyph}</span>
                  <span className="mt-3 font-mono-eav text-[11px] tracking-[0.22em] uppercase text-[#c9bca4]">
                    {stop.label}
                  </span>
                </div>
                {i < journey.length - 1 && (
                  <div className="flex-1 h-px bg-[#2a2620] min-w-[20px]" />
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-12 text-[#c9bca4] max-w-3xl leading-relaxed">
            Most mid-size businesses are stuck between <em className="text-[#f4efe6] not-italic">Invisible</em> and <em className="text-[#f4efe6] not-italic">Described</em>. Your snapshot shows exactly where you are — and what it takes to move.
          </p>
        </div>
      </section>

      {/* ════════ THIS IS NOT SEO ════════ */}
      <section id="not-seo" className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <p className="overline">What this is</p>
              <h2 className="mt-4 font-serif-display text-[34px] sm:text-[48px] leading-[1.05] text-[#f4efe6]" style={{fontWeight: 600}}>
                This is not SEO.
                <br /><em className="text-[#b8963e] italic font-normal">This is how AI understands you.</em>
              </h2>
              <p className="mt-6 text-[#c9bca4] leading-relaxed">
                Search engines rank pages. AI systems build mental models of companies. Those are two completely different problems — and almost no one is working on the second one yet.
              </p>
              <p className="mt-4 text-[#c9bca4] leading-relaxed">
                When a prospect asks ChatGPT who to hire, it draws on entity recognition, category signals, competitive clustering, and authority patterns — not backlinks or meta tags. We measure all of it.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="eav-card overflow-hidden">
                <div className="grid grid-cols-2 px-5 py-3 bg-[#0a0908] border-b border-[#2a2620] font-mono-eav text-[10px] tracking-[0.22em] uppercase">
                  <span className="text-[#8a7e6d]">Traditional SEO</span>
                  <span className="text-[#b8963e]">AI Visibility</span>
                </div>
                {comparison.map(([a, b], i) => (
                  <div key={i} className={`grid grid-cols-2 px-5 py-4 text-sm ${i < comparison.length - 1 ? "border-b border-[#1a1815]" : ""}`}>
                    <span className="text-[#8a7e6d] flex items-center gap-2">
                      <span className="text-[#5e5448] font-mono-eav text-xs">✕</span> {a}
                    </span>
                    <span className="text-[#f4efe6] flex items-center gap-2">
                      <span className="text-[#b8963e] font-mono-eav">✦</span> {b}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ FIVE DIMENSIONS ════════ */}
      <section id="score" className="border-b border-[#2a2620] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p className="overline">The Scoring Framework</p>
          <h2 className="mt-4 font-serif-display text-[36px] sm:text-[56px] leading-[1.05] text-[#f4efe6] max-w-4xl" style={{fontWeight: 600}}>
            Five dimensions. <em className="text-[#b8963e] italic font-normal">One AI Visibility Score™</em>
          </h2>

          <div className="mt-14 grid lg:grid-cols-2 gap-x-12 gap-y-2">
            {dimensions.map((d) => (
              <div key={d.n} className="border-b border-[#2a2620] py-7 last:border-b-0 grid grid-cols-12 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <span className="font-mono-eav text-sm text-[#b8963e] tracking-wider">{d.n}</span>
                </div>
                <div className="col-span-10 sm:col-span-11">
                  <h3 className="font-serif-display text-2xl text-[#f4efe6]" style={{fontWeight: 600}}>{d.title}</h3>
                  <p className="mt-2 text-sm text-[#c9bca4] leading-relaxed">{d.body}</p>
                  <p className="mt-3 font-mono-eav text-[10px] tracking-[0.2em] text-[#5e5448]">0 – 20 PTS</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ THE SCORE / SAMPLE ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-7">
              <p className="overline">AI Visibility Score™</p>
              <h2 className="mt-4 font-serif-display text-[34px] sm:text-[48px] leading-[1.05] text-[#f4efe6]" style={{fontWeight: 600}}>
                A single number. <em className="text-[#b8963e] italic font-normal">A complete picture.</em>
              </h2>
              <p className="mt-6 text-[#c9bca4] leading-relaxed">
                Every snapshot produces a 0–100 score across all five dimensions. It tells you exactly where AI places you — and the gap between where you are and where you need to be.
              </p>
              <p className="mt-4 text-[#c9bca4] leading-relaxed">
                {"Most mid-size businesses score between 20–45. The ones AI actively recommends score 71 or higher. That gap is measurable, and it's fixable."}
              </p>
              <div className="mt-8 space-y-2">
                {tiers.map((t) => (
                  <div key={t.label} className="flex items-baseline gap-4 py-2 border-b border-[#1a1815]">
                    <span className="font-mono-eav text-xs text-[#8a7e6d] w-14 flex-shrink-0">{t.range}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
                      <span className="font-serif-display font-bold text-[#f4efe6] text-sm w-36">{t.label}</span>
                    </div>
                    <span className="text-xs text-[#c9bca4]">{t.body}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample score card */}
            <div className="lg:col-span-5">
              <div className="eav-card p-7 relative">
                <div className="absolute top-0 left-0 right-0 h-px gold-rule" />
                <p className="overline-soft">Sample report</p>
                <div className="mt-4 flex items-end gap-2">
                  <span className="font-serif-display text-[92px] leading-none text-[#f4efe6]" style={{fontWeight: 700}}>34</span>
                  <span className="text-[#5e5448] mb-4 font-mono-eav text-sm">/ 100</span>
                </div>
                <p className="mt-2 font-mono-eav text-xs text-[#d4a04a] tracking-wider">TIER — UNDER-REPRESENTED</p>
                <div className="mt-6 space-y-3.5">
                  {[
                    ["Entity Recognition",       7],
                    ["AI Discovery Presence",    5],
                    ["Competitive Positioning",  3],
                    ["Accuracy & Consistency",  11],
                    ["Authority & Confidence",   8],
                  ].map(([n, v]) => (
                    <div key={n}>
                      <div className="flex justify-between text-xs">
                        <span className="text-[#c9bca4]">{n}</span>
                        <span className="font-mono-eav text-[#f4efe6]">{v}/20</span>
                      </div>
                      <div className="h-[3px] bg-[#1a1815] mt-1.5 overflow-hidden">
                        <div className="h-full bg-[#b8963e] bar-fill" style={{ "--target": v / 20 }} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 font-mono-eav text-[10px] tracking-wider text-[#5e5448]">
                  SAMPLE · MID-SIZE B2B · UNDER-REPRESENTED TIER
                </p>
                <p className="mt-2 text-[11px] italic font-serif-display text-[#8a7e6d]">
                  Full audit identifies every fix, ranked by impact →
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ GAPS & WINS ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p className="overline">What we find</p>
          <h2 className="mt-4 font-serif-display text-[34px] sm:text-[48px] leading-[1.05] text-[#f4efe6] max-w-4xl" style={{fontWeight: 600}}>
            Common gaps — and the <em className="text-[#b8963e] italic font-normal">quick wins</em> that move scores
          </h2>

          <div className="mt-14 grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="font-serif-display text-2xl text-[#f4efe6]" style={{fontWeight: 600}}>Visibility Gaps</h3>
              <p className="mt-2 text-sm text-[#8a7e6d]">The most common reasons AI under-represents a company.</p>
              <div className="mt-6 space-y-4">
                {gaps.map((g) => (
                  <div key={g.title} className="eav-card p-5">
                    <div className="flex items-start gap-3">
                      <span className="text-[#c46a5a] font-serif-display text-2xl leading-none mt-0.5">⊗</span>
                      <div>
                        <h4 className="font-serif-display font-bold text-[#f4efe6]">{g.title}</h4>
                        <p className="mt-1 text-sm text-[#c9bca4] leading-relaxed">{g.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif-display text-2xl text-[#f4efe6]" style={{fontWeight: 600}}>Quick Wins</h3>
              <p className="mt-2 text-sm text-[#8a7e6d]">Five actions that move your score. Customized to your company.</p>
              <div className="mt-6 space-y-4">
                {wins.map((w, i) => (
                  <div key={w.title} className="eav-card p-5">
                    <div className="flex items-start gap-3">
                      <span className="font-mono-eav text-xs text-[#b8963e] mt-1 tracking-wider">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h4 className="font-serif-display font-bold text-[#f4efe6]">{w.title}</h4>
                        <p className="mt-1 text-sm text-[#c9bca4] leading-relaxed">{w.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ HOW IT WORKS ════════ */}
      <section id="how" className="border-b border-[#2a2620] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p className="overline">How it works</p>
          <h2 className="mt-4 font-serif-display text-[34px] sm:text-[48px] leading-[1.05] text-[#f4efe6] max-w-4xl" style={{fontWeight: 600}}>
            Four steps to your <em className="text-[#b8963e] italic font-normal">AI Visibility Score™</em>
          </h2>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s) => (
              <div key={s.n} className="eav-card p-6">
                <p className="font-mono-eav text-xs text-[#8a7e6d] tracking-wider">STEP {s.n}</p>
                <s.icon className="w-6 h-6 text-[#b8963e] mt-4" />
                <h3 className="mt-4 font-serif-display font-bold text-xl text-[#f4efe6]">{s.title}</h3>
                <p className="mt-3 text-sm text-[#c9bca4] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ PLATFORMS AUDITED ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
          <p className="overline text-center">Audited Across · Every Snapshot</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {platforms.map((p) => (
              <div key={p.name} className="eav-card p-6 text-center">
                <p className="font-serif-display font-bold text-2xl text-[#f4efe6]">{p.name}</p>
                <p className="mt-1 font-mono-eav text-[10px] tracking-[0.2em] uppercase text-[#8a7e6d]">{p.by}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ WHY IT MATTERS QUOTE ════════ */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-24 sm:py-32 text-center">
          <p className="overline">Why it matters now</p>
          <blockquote className="mt-8 font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#f4efe6] leading-[1.25] italic" style={{fontWeight: 400}}>
            {`"AI is now the first place prospects go to validate a vendor. If it can't describe you accurately — or doesn't mention you at all — `}
            <em className="text-[#b8963e] not-italic">{"you've lost the deal before it started."}</em>
            {`"`}
          </blockquote>
          <p className="mt-8 font-mono-eav text-xs tracking-[0.22em] uppercase text-[#8a7e6d]">
            — The Entity Agency
          </p>
        </div>
      </section>

      {/* ════════ FROM THE AI VISIBILITY INDEX™ (Research Hub teaser) ════════ */}
      {featured.length > 0 && (
        <section id="research" data-testid="landing-research-section"
                 className="border-b border-[#2a2620] relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full opacity-[0.06] pointer-events-none"
            style={{ background: "radial-gradient(circle at center, #b8963e 0%, transparent 60%)" }}
          />
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 relative">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <p className="overline mb-6">The AI Visibility Index™</p>
                <h2 className="font-serif-display text-[40px] sm:text-[52px] tracking-tight text-[#f4efe6] leading-[1.04]"
                    style={{ fontWeight: 600 }}>
                  Findings, teardowns, and reports <br className="hidden sm:block" />
                  <em className="text-[#b8963e] italic font-normal">from the audit floor.</em>
                </h2>
                <p className="mt-5 text-[#c9bca4] max-w-2xl leading-relaxed">
                  Original research from auditing how ChatGPT, Claude, Gemini, and Perplexity
                  describe and recommend the businesses they evaluate.
                </p>
              </div>
              <Link to="/research" data-testid="landing-research-cta"
                    className="btn-ghost shrink-0">
                Browse the Index <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {featured.map((pub) => (
                <Link
                  key={pub.slug}
                  to={`/research/${pub.category}/${pub.slug}`}
                  data-testid={`landing-research-card-${pub.slug}`}
                  className="group block border border-[#2a2620] rounded-sm p-6 hover:border-[#b8963e]/60 hover:bg-[#0c0a08]/60 transition-all duration-200"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <BookOpen className="w-3.5 h-3.5 text-[#b8963e]" />
                    <span className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">
                      {CATEGORY_LABEL[pub.category] || pub.category}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-[22px] leading-[1.18] text-[#f4efe6] group-hover:text-[#f7e9c8] transition-colors"
                      style={{ fontWeight: 600 }}>
                    {pub.title}
                  </h3>
                  {pub.excerpt && (
                    <p className="mt-3 text-sm text-[#8a7e6d] leading-relaxed line-clamp-3">
                      {pub.excerpt}
                    </p>
                  )}
                  <div className="mt-5 flex items-center gap-1 text-[#b8963e] font-mono-eav text-[11px] tracking-[0.18em] uppercase">
                    Read <MoveUpRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ════════ FAQ ════════ */}
      <FaqSection items={HOMEPAGE_FAQS} testId="landing-faq" />

      {/* ════════ FINAL CTA ════════ */}
      <section className="border-b border-[#2a2620] relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -bottom-40 -left-40 w-[700px] h-[700px] rounded-full opacity-[0.07] pointer-events-none"
          style={{ background: "radial-gradient(circle at center, #b8963e 0%, transparent 60%)" }}
        />
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-28 sm:py-36 text-center relative">
          <p className="overline">Get started</p>
          <h2 className="mt-6 font-serif-display text-[44px] sm:text-[60px] lg:text-[76px] tracking-tight text-[#f4efe6] leading-[1.02]" style={{fontWeight: 600}}>
            Find out where AI <br className="hidden sm:block" />
            places you — <em className="text-[#b8963e] italic font-normal">for free.</em>
          </h2>
          <p className="mt-8 text-[#c9bca4] max-w-2xl mx-auto leading-relaxed">
            Request your AI Visibility Snapshot in 30 seconds. Scored report delivered instantly. No credit card, no pitch, no obligation.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Link to="/signup" data-testid="footer-cta-signup" className="btn-gold">
              Get my free snapshot <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" data-testid="footer-cta-login" className="btn-ghost">
              Sign in
            </Link>
          </div>
          <p className="mt-10 font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#5e5448]">
            Clarity in AI · Visibility that grows
          </p>
        </div>
      </section>
    </div>
  );
}
